import { createSlice } from "@reduxjs/toolkit";

/* -------------------------------------------------------------------------- */
/* Initial State                                                              */
/* -------------------------------------------------------------------------- */

const currentDate = new Date().toISOString().slice(0, 10);

export const initialContractState = {
  title: "Contract 01",
  contractType: null,
  invoiceAddress: null,
  startDate: currentDate,
  endDate: null,
  notes: null,
  invoiceInterval: null,
  priceIncludesVat: false,
  insurancePercentage: null,
  invoiceDescription: null,
  useUnitsForInvoiceValue: false,
  listAssignedUnitsOnInvoice: false,
  paymentMethod: null,
  rate: null,
  ratePeriod: null,
  rateDescription: null,
  status: "In storage",
  sites: [],
};

interface StorageNotification {
  inventory_id?: string;
  customer_name?: string;
  lead_id?: string;
  isRead?: boolean;
}

interface StorageNotificationResponse {
  list: StorageNotification[];
  pagination?: {
    totalItems?: number;
    currentPage?: number;
    totalPages?: number;
  };
}

const initialState = {
  contractList: [],
  contractPagination: [],
  isCreateContractResponse: false,
  singleContractDetail: null,
  customerStorageResponse: null,
  isInvoiceGenerated: false,
  isDeletedContractResponse: false,
  isEmailContractResponse: false,
  isPrintContractResponse: false,
  isFinalInvoiceGenerateToMoveJob: false,
  checkSerivceForContract: null,
  isMoveToFinalResponse: false,
  isDeletedContractUnitResponse: false,
  isSendSignLinkResponse: false,
  contractSignDetails: {},
  isContractSignUpdated: false,
  storageNotification: null as StorageNotificationResponse | null,
};
/* -------------------------------------------------------------------------- */
/* Contract Storage Slice                                                              */
/* -------------------------------------------------------------------------- */

export const contractStorageSlice = createSlice({
  name: "contractStorage",
  initialState,
  reducers: {
    getContractListAction: (state, { payload }) => {},
    setContractListAction: (state, { payload }) => {
      state.contractList = payload?.list;
      state.contractPagination = payload?.pagination;
    },

    createContractAction: (state, { payload }) => {},
    setCreateContractResponseAction: (state, { payload }) => {
      state.isCreateContractResponse = payload;
    },

    getSingleContractAction: (state, { payload }) => {},
    setSingleContractAction: (state, { payload }) => {
      state.singleContractDetail = payload;
    },

    deleteContractAction: (state, { payload }) => {},
    setDeleteContractResponseAction: (state, { payload }) => {
      state.isDeletedContractResponse = payload;
    },
    emailContractAction: (state, { payload }) => {},
    printContractAction: (state, { payload }) => {},
    setEmailContractResponseAction: (state, { payload }) => {
      state.isEmailContractResponse = payload;
    },
    setPrintContractResponseAction: (state, { payload }) => {
      state.isPrintContractResponse = payload;
    },

    getStorageNotificationAction: (state, { payload }) => {},
    setStorageNotificationResponse: (state, { payload }) => {
      state.storageNotification = payload;
    },
    markStorageNotificationReadAction: (state, { payload }) => {},
    setStorageNotificationReadResponse: (state, { payload }) => {
      const notification = state.storageNotification?.list?.find(
        (item) => item.inventory_id === payload,
      );
      if (notification) {
        notification.isRead = true;
      }
    },

    updateContractAction: (state, { payload }) => {},

    /* ---------------------------------------------------------------------- */ /* Clear Single Contract Detail */ /* ---------------------------------------------------------------------- */
    clearSingleContractDetailAction: (state) => {
      state.singleContractDetail = null;
    },
    /* ---------------------------------------------------------------------- */ /* Reset Create Contract Response */ /* ---------------------------------------------------------------------- */
    resetCreateContractResponseAction: (state) => {
      state.isCreateContractResponse = false;
    },

    checkCustomerStorageAction: (state, { payload }) => {},
    setCheckCustomerStorageResponseAction: (state, { payload }) => {
      state.customerStorageResponse = payload;
    },

    clearCustomerStorageCheckResponse: (state) => {
      state.customerStorageResponse = null;
    },

    invoiceGeneratedAction: (state, { payload }) => {},
    setInvoiceGeneratedResponse: (state, { payload }) => {
      state.isInvoiceGenerated = payload;
    },

    //  Generate final invoice to move to job
    invoiceGenerateForMoveToJob: (state, { payload }) => {},
    setInvoiceGeneratedForMoveToJobResponse: (state, { payload }) => {
      state.isFinalInvoiceGenerateToMoveJob = payload;
    },

    checkServiceForContractAction: (state, { payload }) => {},
    setCheckServiceForContractResponse: (state, { payload }) => {
      state.checkSerivceForContract = payload;
    },

    moveToJobFinalAction: (state, { payload }) => {},
    setMoveToJobFinalResponse: (state, { payload }) => {
      state.isMoveToFinalResponse = payload;
    },

    loadStorageInvoice: (state, { payload }) => {},

    deleteContractUnitAction: (state, { payload }) => {},
    setDeleteContractUnitResponse: (state, { payload }) => {
      state.isDeletedContractUnitResponse = payload;
    },

    sendContractSignLinkAction: (state, { payload }) => {},
    setSendContractSignLinkResponse: (state, { payload }) => {
      state.isSendSignLinkResponse = payload;
    },

    getContractSignDetailsAction: (state, { payload }) => {},
    setContractSignDetailsResponse: (state, { payload }) => {
      state.contractSignDetails = payload;
    },

    updateContractSignAction: (state, { payload }) => {},
    setUpdateContractSignResponse: (state, { payload }) => {
      state.isContractSignUpdated = payload;
    },
  },
});

/* -------------------------------------------------------------------------- */
/* Reducer                                                                    */
/* -------------------------------------------------------------------------- */

const {
  getContractListAction,
  setContractListAction,
  createContractAction,
  updateContractAction,
  deleteContractAction,
  setDeleteContractResponseAction,
  emailContractAction,
  printContractAction,
  getStorageNotificationAction,
  setStorageNotificationResponse,
  markStorageNotificationReadAction,
  setStorageNotificationReadResponse,
  setEmailContractResponseAction,
  setPrintContractResponseAction,
  setCreateContractResponseAction,
  deleteContractUnitAction,
  setDeleteContractUnitResponse,
  getSingleContractAction,
  setSingleContractAction,
  clearSingleContractDetailAction,
  resetCreateContractResponseAction,
  checkCustomerStorageAction,
  setCheckCustomerStorageResponseAction,
  clearCustomerStorageCheckResponse,
  invoiceGeneratedAction,
  setInvoiceGeneratedResponse,
  invoiceGenerateForMoveToJob,
  setInvoiceGeneratedForMoveToJobResponse,
  checkServiceForContractAction,
  setCheckServiceForContractResponse,
  moveToJobFinalAction,
  setMoveToJobFinalResponse,
  sendContractSignLinkAction,
  setSendContractSignLinkResponse,
  getContractSignDetailsAction,
  setContractSignDetailsResponse,
  updateContractSignAction,
  setUpdateContractSignResponse,
} = contractStorageSlice.actions;
export {
  getContractListAction,
  setContractListAction,
  createContractAction,
  updateContractAction,
  deleteContractAction,
  setDeleteContractResponseAction,
  emailContractAction,
  printContractAction,
  getStorageNotificationAction,
  setStorageNotificationResponse,
  markStorageNotificationReadAction,
  setStorageNotificationReadResponse,
  setEmailContractResponseAction,
  setPrintContractResponseAction,
  setCreateContractResponseAction,
  deleteContractUnitAction,
  setDeleteContractUnitResponse,
  getSingleContractAction,
  setSingleContractAction,
  clearSingleContractDetailAction,
  resetCreateContractResponseAction,
  checkCustomerStorageAction,
  setCheckCustomerStorageResponseAction,
  clearCustomerStorageCheckResponse,
  invoiceGeneratedAction,
  setInvoiceGeneratedResponse,
  invoiceGenerateForMoveToJob,
  setInvoiceGeneratedForMoveToJobResponse,
  checkServiceForContractAction,
  setCheckServiceForContractResponse,
  moveToJobFinalAction,
  setMoveToJobFinalResponse,
  sendContractSignLinkAction,
  setSendContractSignLinkResponse,
  getContractSignDetailsAction,
  setContractSignDetailsResponse,
  updateContractSignAction,
  setUpdateContractSignResponse,
};
export default contractStorageSlice.reducer;
